import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { Divider, NgButtonNavLink } from '@/ui';
import type { LiveVideo } from '@/signalr';
import { usePsapConfiguration } from '@/app/shell/psapConfiguration';
import { checkRecordingExists } from '@/lib/video/recordingAvailabilityService';
import { incidentRecordingDetailRoute, incidentRecordingsRoute } from '@/features/recordings/routes';
import { getLatestRecording, showRecordings } from './videoRecordings';

export type VideoRecordingsBoxProps = {
  incidentId: string;
  liveVideos: readonly LiveVideo[];
  allowDivider?: boolean;
};

export function VideoRecordingsBox({ incidentId, liveVideos, allowDivider = false }: VideoRecordingsBoxProps) {
  const { t } = useTranslation();
  const psapConfiguration = usePsapConfiguration();

  const latestRecording = getLatestRecording(liveVideos);
  const shouldShowRecordings = showRecordings(latestRecording);
  const showDivider = allowDivider && latestRecording !== null;

  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true);
  const [recordingBlobExists, setRecordingBlobExists] = useState(false);

  const latestRecordingId = latestRecording?.id ?? null;
  const availabilityCheckEnabled =
    latestRecordingId !== null && psapConfiguration.videoRecordingEnabled && shouldShowRecordings;

  useEffect(() => {
    if (!availabilityCheckEnabled || latestRecordingId === null) {
      return;
    }

    let cancelled = false;
    void checkRecordingExists(latestRecordingId).then((exists) => {
      if (!cancelled) {
        setRecordingBlobExists(exists);
        setIsCheckingAvailability(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [availabilityCheckEnabled, latestRecordingId]);

  return (
    <>
      {latestRecording !== null && (
        <div className="d-flex flex-column align-items-center gap-1">
          {psapConfiguration.videoRecordingEnabled && shouldShowRecordings ? (
            <>
              {isCheckingAvailability ? (
                <div className="small text-muted">
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                </div>
              ) : recordingBlobExists ? (
                <>
                  <div className="small">{t('Incident_VideoRecordings_LatestRecording_Label')}</div>

                  <NgButtonNavLink
                    to={incidentRecordingDetailRoute(incidentId, latestRecording.id)}
                    text={t('Button_Play')}
                    size="small"
                    icon="fal fa-play"
                  />
                </>
              ) : (
                <div className="small">{t('Incident_VideoRecordings_NotAvailable')}</div>
              )}

              {liveVideos.length > 1 && (
                <div className="small">
                  <NavLink to={incidentRecordingsRoute(incidentId)}>
                    {t('Incident_VideoRecordings_ShowAllRecordings')}
                    <span className="badge bg-dark ms-1">{liveVideos.length}</span>
                  </NavLink>
                </div>
              )}
            </>
          ) : (
            <div className="small">{t('Incident_VideoRecordings_NotAvailable')}</div>
          )}
        </div>
      )}

      {showDivider && <Divider />}
    </>
  );
}
